import { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import { CalendarClock, ChevronDown, Loader2, Plus, Trash2 } from 'lucide-react';
import { checkinRhythmusAPI } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';

/* W2.5 Check-in-Rhythmus & Urlaub (29.10.2026): Karte für Ernährung › Mitglied (CRM), die Kundenseite auf /coach und die
   Check-in-Auswertung. Rhythmus wöchentlich / 14-tägig / monatlich mit Wochentag und Uhrzeit (in der Beschleunigungsphase
   gilt automatisch wöchentlich), Urlaub (keine Erinnerungen, nichts zählt als überfällig), Check-in einschieben,
   Startwerte nachtragen und — wo die Ernährungsrouten erreichbar sind — der Fragebogen je Kunde.
   Rechnen und prüfen tut der Server (ernaehrung/checkin_rhythmus.py). `api` tauscht die Aufrufe (Trainer-Client der
   Inbox); ohne `api.fragebogen` fehlt der Fragebogen-Teil. */

const TAGE = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'];
const TAGE_LANG = ['Montag', 'Dienstag', 'Mittwoch', 'Donnerstag', 'Freitag', 'Samstag', 'Sonntag'];
const RHYTHMEN = [['woche', 'Wöchentlich'], ['zwei_wochen', 'Alle 2 Wochen'], ['monat', 'Monatlich']];
const TYPEN = [['skala', 'Skala 1–10'], ['zahl', 'Zahl'], ['text', 'Text'], ['auswahl', 'Auswahl'], ['foto', 'Foto']];
const fehlertext = (e, rest = 'Das hat nicht geklappt') => (typeof e?.response?.data?.detail === 'string' ? e.response.data.detail : rest);
const tagDe = (iso) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString('de-DE', { weekday: 'short', day: '2-digit', month: '2-digit' }) : '');
const heuteIso = () => new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Berlin' });
const STANDARD_API = checkinRhythmusAPI;

function Knopf({ an, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={an} data-testid={testid}
      className={`min-h-11 rounded-[10px] border px-3 text-sm font-semibold transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${an ? 'border-marke bg-marke/10 text-foreground' : 'border-border text-muted-foreground hover:bg-muted'}`}>
      {children}
    </button>
  );
}

function Aufklapper({ titel, offen, onToggle, children, testid }) {
  return (
    <div className="rounded-[12px] border border-border/70">
      <button type="button" onClick={onToggle} aria-expanded={offen} data-testid={testid}
        className="flex min-h-11 w-full items-center justify-between gap-2 px-3 text-left text-sm font-bold focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
        {titel}<ChevronDown className={`h-4 w-4 transition-transform ${offen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>
      {offen && <div className="space-y-3 border-t border-border/60 p-3">{children}</div>}
    </div>
  );
}

function Fragebogen({ leadId, d, api, onNeu }) {
  const std = d.standardfelder || [];
  const aktuell = d.fragebogen;
  const eigen = aktuell?.fuer_lead_id === leadId;
  const [felder, setFelder] = useState(() => (eigen ? aktuell.fragen.filter((q) => std.some((s) => s.id === q.id)).map((q) => q.id) : std.map((s) => s.id)));
  const [fragen, setFragen] = useState(() => (eigen ? aktuell.fragen.filter((q) => !std.some((s) => s.id === q.id)).map((q) => ({
    typ: q.typ === 'gewicht' ? 'zahl' : q.typ, text: q.text, pflicht: !!q.pflicht, optionen: (q.optionen || []).join(', '), einheit: q.einheit || '' })) : []));
  const [busy, setBusy] = useState(false);
  const umschalten = (id) => setFelder((f) => (f.includes(id) ? f.filter((x) => x !== id) : [...f, id]));
  const frage = (i, teil) => setFragen((l) => l.map((q, j) => (j === i ? { ...q, ...teil } : q)));
  const speichern = async () => {
    setBusy(true);
    try {
      await api.fragebogen(leadId, {
        art: 'checkin', standardfelder: std.filter((s) => felder.includes(s.id)).map((s) => s.id),
        fragen: fragen.filter((q) => q.text.trim()).map((q) => ({ typ: q.typ, text: q.text.trim(), pflicht: q.pflicht, einheit: q.einheit || null,
          optionen: q.typ === 'auswahl' ? q.optionen.split(',').map((o) => o.trim()).filter(Boolean) : null })),
      });
      toast.success('Fragebogen gespeichert – gilt ab dem nächsten Check-in');
      onNeu();
    } catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-3" data-testid="checkin-fragebogen">
      <p className="text-xs text-muted-foreground">Aktuell: <span className="font-semibold text-foreground">{aktuell?.titel || '—'}</span>{eigen ? ' (nur für diesen Kunden)' : ' (Studio-Vorlage)'}</p>
      <div className="flex flex-wrap gap-2">
        {std.map((s) => <Knopf key={s.id} an={felder.includes(s.id)} onClick={() => umschalten(s.id)} testid={`fb-std-${s.id}`}>{s.text.replace(/\?$/, '')}</Knopf>)}
      </div>
      {fragen.map((q, i) => (
        // eslint-disable-next-line react/no-array-index-key
        <div key={i} className="grid gap-2 rounded-[10px] bg-muted/40 p-2 sm:grid-cols-[8rem_1fr_auto]">
          <select value={q.typ} onChange={(e) => frage(i, { typ: e.target.value })} aria-label="Fragetyp" className="h-11 rounded-md border border-input bg-background px-2 text-sm">
            {TYPEN.map(([w, l]) => <option key={w} value={w}>{l}</option>)}
          </select>
          <Input value={q.text} onChange={(e) => frage(i, { text: e.target.value })} placeholder="Frage" aria-label="Frage" className="h-11" />
          <div className="flex items-center gap-2">
            <label className="flex items-center gap-1.5 text-xs"><input type="checkbox" checked={q.pflicht} onChange={(e) => frage(i, { pflicht: e.target.checked })} />Pflicht</label>
            <Button type="button" variant="ghost" size="icon" className="h-11 w-11" onClick={() => setFragen((l) => l.filter((_, j) => j !== i))} aria-label="Frage entfernen"><Trash2 className="h-4 w-4" /></Button>
          </div>
          {q.typ === 'auswahl' && <Input value={q.optionen} onChange={(e) => frage(i, { optionen: e.target.value })} placeholder="Antworten, mit Komma getrennt" aria-label="Antwortmöglichkeiten" className="h-11 sm:col-span-3" />}
          {q.typ === 'zahl' && <Input value={q.einheit} onChange={(e) => frage(i, { einheit: e.target.value })} placeholder="Einheit (optional)" aria-label="Einheit" className="h-11 sm:col-span-3" />}
        </div>
      ))}
      <div className="flex flex-wrap gap-2">
        <Button type="button" variant="outline" className="min-h-11 gap-1.5" onClick={() => setFragen((l) => [...l, { typ: 'skala', text: '', pflicht: false, optionen: '', einheit: '' }])} data-testid="fb-frage-dazu"><Plus className="h-4 w-4" /> Eigene Frage</Button>
        <Button type="button" className="min-h-11" onClick={speichern} disabled={busy} data-testid="fb-speichern">{busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}Fragebogen speichern</Button>
      </div>
    </div>
  );
}

export function CheckinRhythmusKarte({ leadId, api = STANDARD_API, onGeaendert }) {
  const [d, setD] = useState(null);
  const [err, setErr] = useState('');
  const [f, setF] = useState(null);
  const [busy, setBusy] = useState(false);
  const [neuU, setNeuU] = useState({ von: '', bis: '' });
  const [sw, setSw] = useState({ datum: '', gewicht: '', taille: '', huefte: '' });
  const [offen, setOffen] = useState('');
  const uebernehmen = (r) => {
    setD(r);
    setF({ rhythmus: r.rhythmus, wochentag: r.wochentag, uhrzeit: r.uhrzeit, erinnerung: r.erinnerung, eigener: r.eigener_zeitplan });
  };
  const laden = useCallback(() => api.einstellung(leadId).then((r) => { uebernehmen(r.data); setErr(''); })
    .catch((e) => setErr(fehlertext(e, 'Check-in-Einstellung nicht geladen'))), [api, leadId]);
  useEffect(() => { laden(); }, [laden]);

  if (err) return <Card className="border-border/60"><CardContent className="py-4 text-sm text-muted-foreground" role="status">{err}</CardContent></Card>;
  if (!d || !f) return null;
  const nurCoaching = d.systeme && d.systeme.ernaehrung === false;
  const phase = d.wirksam?.quelle === 'phase';

  const speichern = async (teil, meldung = 'Gespeichert') => {
    setBusy(true);
    try { const r = await api.speichern(leadId, teil); uebernehmen(r.data); toast.success(meldung); onGeaendert?.(); }
    catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };
  const rhythmusSpeichern = () => speichern({ rhythmus: f.rhythmus, erinnerung: f.erinnerung,
    zeitplan: f.eigener || f.wochentag !== d.standard?.wochentag || f.uhrzeit !== d.standard?.uhrzeit ? { wochentag: f.wochentag, uhrzeit: f.uhrzeit } : null });
  const urlaubDazu = () => {
    if (!neuU.von || !neuU.bis) { toast.error('Von und Bis angeben'); return; }
    speichern({ urlaub: [...d.urlaub, neuU] }, 'Urlaub eingetragen');
    setNeuU({ von: '', bis: '' });
  };
  const einschieben = async () => {
    setBusy(true);
    try { await api.einschieben(leadId); toast.success('Check-in eingeschoben – das Mitglied bekommt einen Hinweis in der App'); onGeaendert?.(); }
    catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };
  const startwerte = async () => {
    const umfaenge = {};
    if (sw.taille) umfaenge.taille = Number(String(sw.taille).replace(',', '.'));
    if (sw.huefte) umfaenge.huefte = Number(String(sw.huefte).replace(',', '.'));
    setBusy(true);
    try {
      await api.startwerte(leadId, { datum: sw.datum || null, gewicht: sw.gewicht ? Number(String(sw.gewicht).replace(',', '.')) : null, umfaenge });
      toast.success('Startwerte eingetragen'); setSw({ datum: '', gewicht: '', taille: '', huefte: '' }); onGeaendert?.();
    } catch (e) { toast.error(fehlertext(e)); } finally { setBusy(false); }
  };

  return (
    <Card className="border-border/60" data-testid="checkin-rhythmus-karte">
      <CardHeader className="pb-2">
        <CardTitle className="flex items-center gap-2 text-base"><CalendarClock className="h-4 w-4 text-marke" aria-hidden="true" /> Check-in-Rhythmus & Urlaub</CardTitle>
        <p className="text-xs text-muted-foreground">
          {d.urlaub_jetzt?.laeuft ? `Im Urlaub bis ${tagDe(d.urlaub_jetzt.bis)} – keine Erinnerungen, nichts zählt als überfällig.`
            : nurCoaching ? 'P1 Coaching: Check-in jede Woche am Wochentag des Starttermins.'
              : d.aktiv ? `Nächster Check-in: ${d.naechster ? tagDe(d.naechster) : '—'}` : 'Der wöchentliche Check-in ist im Studio ausgeschaltet.'}
        </p>
      </CardHeader>
      <CardContent className="space-y-4">
        {!nurCoaching && (
          <div className="space-y-3">
            {phase && <p className="rounded-[10px] border border-marke/40 bg-marke/5 px-3 py-2 text-sm" role="status">Beschleunigungsphase: wöchentlich am {TAGE_LANG[d.wirksam.wochentag]} – der eigene Rhythmus gilt danach wieder.</p>}
            <div className="flex flex-wrap gap-2" role="group" aria-label="Rhythmus">
              {RHYTHMEN.map(([w, l]) => <Knopf key={w} an={f.rhythmus === w} onClick={() => setF({ ...f, rhythmus: w })} testid={`rhythmus-${w}`}>{l}</Knopf>)}
            </div>
            <div className="flex flex-wrap gap-1.5" role="group" aria-label="Wochentag">
              {TAGE.map((t, i) => <Knopf key={t} an={f.wochentag === i} onClick={() => setF({ ...f, wochentag: i, eigener: true })} testid={`wochentag-${i}`}>{t}</Knopf>)}
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <label className="flex items-center gap-2 text-sm">Uhrzeit
                <Input type="time" value={f.uhrzeit} onChange={(e) => setF({ ...f, uhrzeit: e.target.value, eigener: true })} className="h-11 w-28" aria-label="Uhrzeit" />
              </label>
              <label className="flex items-center gap-2 text-sm"><Switch checked={f.erinnerung} onCheckedChange={(v) => setF({ ...f, erinnerung: v })} aria-label="Erinnerung per Push" />Erinnerung (Vorabend + am Tag)</label>
              <Button type="button" className="min-h-11" onClick={rhythmusSpeichern} disabled={busy} data-testid="rhythmus-speichern">Speichern</Button>
            </div>
          </div>
        )}

        <div className="space-y-2">
          <p className="text-sm font-bold">Urlaub</p>
          {d.urlaub.length === 0 && <p className="text-xs text-muted-foreground">Kein Urlaub eingetragen.</p>}
          {d.urlaub.map((u) => (
            <div key={`${u.von}-${u.bis}`} className="flex items-center justify-between gap-2 rounded-[10px] bg-muted/40 px-3 py-1.5 text-sm">
              <span>{tagDe(u.von)} – {tagDe(u.bis)}</span>
              <Button type="button" variant="ghost" size="icon" className="h-11 w-11" disabled={busy} aria-label="Urlaub entfernen"
                onClick={() => speichern({ urlaub: d.urlaub.filter((x) => x !== u) }, 'Urlaub entfernt')}><Trash2 className="h-4 w-4" /></Button>
            </div>
          ))}
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-xs">Von<Input type="date" value={neuU.von} min={heuteIso()} onChange={(e) => setNeuU({ ...neuU, von: e.target.value })} className="h-11" /></label>
            <label className="text-xs">Bis<Input type="date" value={neuU.bis} min={neuU.von || heuteIso()} onChange={(e) => setNeuU({ ...neuU, bis: e.target.value })} className="h-11" /></label>
            <Button type="button" variant="outline" className="min-h-11 gap-1.5" onClick={urlaubDazu} disabled={busy} data-testid="urlaub-dazu"><Plus className="h-4 w-4" /> Urlaub</Button>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" className="min-h-11" onClick={einschieben} disabled={busy} data-testid="checkin-einschieben">Check-in einschieben</Button>
        </div>

        <Aufklapper titel="Startwerte nachtragen" offen={offen === 'start'} onToggle={() => setOffen(offen === 'start' ? '' : 'start')} testid="startwerte-auf">
          <p className="text-xs text-muted-foreground">Für Bestandskunden: Gewicht und Umfänge zu einem früheren Tag. Start- und Zielgewicht im Profil bleiben, wie sie sind.</p>
          <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
            <label className="text-xs">Datum<Input type="date" value={sw.datum} max={heuteIso()} onChange={(e) => setSw({ ...sw, datum: e.target.value })} className="h-11" /></label>
            <label className="text-xs">Gewicht (kg)<Input inputMode="decimal" value={sw.gewicht} onChange={(e) => setSw({ ...sw, gewicht: e.target.value })} className="h-11" /></label>
            <label className="text-xs">Taille (cm)<Input inputMode="decimal" value={sw.taille} onChange={(e) => setSw({ ...sw, taille: e.target.value })} className="h-11" /></label>
            <label className="text-xs">Hüfte (cm)<Input inputMode="decimal" value={sw.huefte} onChange={(e) => setSw({ ...sw, huefte: e.target.value })} className="h-11" /></label>
          </div>
          <Button type="button" className="min-h-11" onClick={startwerte} disabled={busy} data-testid="startwerte-speichern">Startwerte speichern</Button>
        </Aufklapper>

        {api.fragebogen && !nurCoaching && (
          <Aufklapper titel="Fragebogen" offen={offen === 'fb'} onToggle={() => setOffen(offen === 'fb' ? '' : 'fb')} testid="fragebogen-auf">
            <Fragebogen leadId={leadId} d={d} api={api} onNeu={laden} />
          </Aufklapper>
        )}
      </CardContent>
    </Card>
  );
}

export default CheckinRhythmusKarte;
